import moment from 'moment';
import { get, map, set, omitBy } from 'lodash/fp';
import { toastr } from 'react-redux-toastr';
import { all, call, select, put } from 'redux-saga/effects';
import * as Api from '../../../../services/fbm-api';
import { sagaRoutineRequest } from '../../../../helpers/saga';
import { getFormPackageItems } from '../../selectors/components/item-form';
import {
  fetchInventoryPackage as fetchInventoryPackageRoutine,
  fetchInventoryPackages as fetchInventoryPackagesRoutine,
  createInventoryPackage as createInventoryPackageRoutine,
  updateInventoryPackage as updateInventoryPackageRoutine,
  deleteInventoryPackage as deleteInventoryPackageRoutine,
  addInventoryPackageItem as addInventoryPackageItemRoutine,
  updateInventoryPackageItem as updateInventoryPackageItemRoutine,
  deleteInventoryPackageItem as deleteInventoryPackageItemRoutine,
  fetchInventoryPackageAvailability as fetchInventoryPackageAvailabilityRoutine,
  fetchInventoryPackagesAvailability as fetchInventoryPackagesAvailabilityRoutine,
  deleteInventoryPackageAvailability as deleteInventoryPackageAvailabilityRoutine,
  fetchInventoryPackageSkus as fetchInventoryPackageSkusRoutine,
  addInventoryPackageSku as addInventoryPackageSkuRoutine,
  deleteInventoryPackageSku as deleteInventoryPackageSkuRoutine
} from './routines';
import history from '../../../../routes/history';
import createItemPathFor from '../../../../helpers/createItemPathFor';
import { resetInventoryPackageDeleted } from '../../actions';

// PackageItems Saga
export const addInventoryPackageItem = ({
  payload: { id, item, location, zone, quantity }
}) =>
  sagaRoutineRequest(addInventoryPackageItemRoutine, {
    request: () =>
      call(Api.addInventoryPackageItem, {
        id,
        itemId: item.id,
        locationId: location.id,
        zoneId: zone.id,
        quantity
      })
  });

export const updateInventoryPackageItem = ({
  payload: { id, packageItemId, quantity }
}) =>
  sagaRoutineRequest(updateInventoryPackageItemRoutine, {
    request: () =>
      call(Api.updateInventoryPackageItem, { id, packageItemId, quantity })
  });

export const deleteInventoryPackageItem = ({
  payload: { id, packageItemId }
}) =>
  sagaRoutineRequest(deleteInventoryPackageItemRoutine, {
    request: () => call(Api.deleteInventoryPackageItem, { id, packageItemId }),
    successPayload: { packageItemId }
  });
// -- PackageItems Saga

export const fetchInventoryPackage = ({ payload: id }) =>
  sagaRoutineRequest(fetchInventoryPackageRoutine, {
    request: () => call(Api.fetchInventoryPackage, id)
  });

export const fetchInventoryPackages = ({ payload = {} }) => {
  const qs = omitBy(each => !each, payload);
  const createdAt = get('createdAt', qs);
  const formatDates = map(each => moment(each).format('YYYY-MM-DD'));
  const options = createdAt
    ? { qs: set('createdAt', formatDates(createdAt), qs) }
    : { qs };

  return sagaRoutineRequest(fetchInventoryPackagesRoutine, {
    request: () => call(Api.fetchInventoryPackages, options)
  });
};

export const createInventoryPackage = ({ payload: body }) =>
  sagaRoutineRequest(createInventoryPackageRoutine, {
    request: () => call(Api.createInventoryPackage, { body }),
    *success(response) {
      const { id } = response;

      // Add PackageItems
      const packageItems = yield select(getFormPackageItems);
      yield all(
        map(
          ({ item, location, zone, quantity }) =>
            call(addInventoryPackageItem, {
              payload: { id, item, location, zone, quantity }
            }),
          packageItems
        )
      );

      yield toastr.success('Success', `Package created.`);
      history.push(createItemPathFor('view', response));
    }
  });

export const updateInventoryPackage = ({ payload: { id, body } }) =>
  sagaRoutineRequest(updateInventoryPackageRoutine, {
    request: () => call(Api.updateInventoryPackage, { id, body }),
    *success() {
      const packageItems = yield select(getFormPackageItems);
      yield all(
        map(
          ({
            id: packageItemId,
            item,
            location,
            zone,
            quantity,
            deleted,
            added,
            edited
          }) => {
            if (deleted)
              return call(deleteInventoryPackageItem, {
                payload: { id, packageItemId }
              });
            if (edited)
              return call(updateInventoryPackageItem, {
                payload: { id, packageItemId, quantity }
              });
            if (added)
              return call(addInventoryPackageItem, {
                payload: { id, item, location, zone, quantity }
              });
            return null;
          },
          packageItems
        )
      );

      yield toastr.success('Success', `Package updated.`);
    }
  });

export const deleteInventoryPackage = ({ payload: { name, id } }) =>
  sagaRoutineRequest(deleteInventoryPackageRoutine, {
    request: () => call(Api.deleteInventoryPackage, id),
    successPayload: { id },
    *success() {
      yield toastr.success('Success', `Deleted package "${name}".`);
    },
    *fulfill() {
      yield put(resetInventoryPackageDeleted());
    }
  });

export const fetchInventoryPackageAvailability = ({ payload: id }) =>
  sagaRoutineRequest(fetchInventoryPackageAvailabilityRoutine, {
    request: () => call(Api.fetchInventoryPackageAvailability, id),
    successPayload: { id }
  });

export const fetchInventoryPackagesAvailability = ({ payload = {} }) => {
  const qs = omitBy(each => !each, payload);
  const createdAt = get('createdAt', qs);
  const formatDates = map(each => moment(each).format('YYYY-MM-DD'));
  const options = createdAt
    ? { qs: set('createdAt', formatDates(createdAt), qs) }
    : { qs };
  return sagaRoutineRequest(fetchInventoryPackagesAvailabilityRoutine, {
    request: () => call(Api.fetchInventoryPackagesAvailability, options)
  });
};

export const deleteInventoryPackageAvailability = ({ payload }) =>
  sagaRoutineRequest(deleteInventoryPackageAvailabilityRoutine, {
    request: () => {
      const id = get('item.id', payload);
      const locationId = get('location.id', payload);
      return call(Api.deleteInventoryPackageAvailability, { id, locationId });
    },
    *success() {
      yield toastr.success(
        'Success',
        'Item availability successfully deleted.'
      );
    }
  });

export const fetchInventoryPackageSkus = ({ payload: id }) =>
  sagaRoutineRequest(fetchInventoryPackageSkusRoutine, {
    request: () => call(Api.fetchInventoryPackageSkus, id)
  });

export const addInventoryPackageSku = ({ payload: { id, sku } }) =>
  sagaRoutineRequest(addInventoryPackageSkuRoutine, {
    request: () => call(Api.addInventoryPackageSku, { id, body: { sku } }),
    *success() {
      yield toastr.success('Success', `Sku has been added.`);
    }
  });

export const deleteInventoryPackageSku = ({ payload: { id, sku, skuId } }) =>
  sagaRoutineRequest(deleteInventoryPackageSkuRoutine, {
    request: () => call(Api.deleteInventoryPackageSku, { id, skuId }),
    successPayload: { id, skuId },
    *success() {
      yield toastr.success('Success', `Deleted sku "${sku}".`);
    }
  });
